import { useEffect, useMemo, useState } from 'react';
/* eslint-disable no-await-in-loop */

import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { addQueueItem } from '../idb';
import { toast } from 'sonner';

export default function PortalLogs({ user }) {
 const [projects, setProjects] = useState([]);

 const [selectedProject, setSelectedProject] = useState('');
 const [buildings, setBuildings] = useState([]);
 const [units, setUnits] = useState([]);
 const [trades, setTrades] = useState([]);
 const [stats, setStats] = useState({ pending: 0, verified: 0, rejected: 0 });

 const [form, setForm] = useState({ unit_id: '', trade: '', qty_done: '', uom: '', pct_complete: 0, notes: '', files: [] });

 useEffect(() => { loadProjectsAndTrades(); }, []);
 useEffect(() => { if (selectedProject) loadProjectData(selectedProject); }, [selectedProject]);

 const loadProjectsAndTrades = async () => {
 try {
 const [tradesRes, tasksRes] = await Promise.all([
 axios.get('/trades'),
 axios.get('/tasks'), // Get contractor's assigned tasks
 ]);
 
 const allTrades = tradesRes.data;
 const tasks = tasksRes.data;
 
 if (tasks.length === 0) {
 toast.message("No tasks assigned yet.");
 return;
 }
 
 // Get only trades that the contractor has tasks for
 const assignedTradeIds = new Set(tasks.map(t => t.trade_id));
 const assignedTrades = allTrades.filter(trade => assignedTradeIds.has(trade.id));
 
 // Set trade names for the dropdown
 setTrades(assignedTrades.map((t) => t.name));
 
 // Get unique unit IDs from tasks
 const unitIds = [...new Set(tasks.map(t => t.unit_id))];
 
 // Get all units and buildings
 const [unitsRes, buildingsRes] = await Promise.all([
 axios.get("/units"),
 axios.get("/buildings")
 ]);
 
 const units = unitsRes.data;
 const buildings = buildingsRes.data;
 
 // Map unit -> building -> project
 const projectIds = new Set();
 tasks.forEach(task => {
 const unit = units.find(u => u.id === task.unit_id);
 if (unit) {
 const building = buildings.find(b => b.id === unit.building_id);
 if (building && building.project_id) {
 projectIds.add(building.project_id);
 }
 }
 });
 
 // Get projects
 const projectsRes = await axios.get("/projects");
 const allProjects = projectsRes.data;
 const contractorProjects = allProjects.filter(p => projectIds.has(p.id));
 
 setProjects(contractorProjects);
 if (contractorProjects.length > 0) {
 setSelectedProject(contractorProjects[0].id);
 } else {
 toast.message("No projects found for your tasks.");
 }
 } catch (e) { 
 toast.error('Failed to load project/trades');
 console.error(e);
 }
 };
 const reloadStats = async (projectId) => {
 try {
 const res = await axios.get(`/cycle3/progress-logs?project_id=${projectId}`);
 const data = res.data || [];
 const pending = data.filter((l) => l.status === 'pending').length;
 const verified = data.filter((l) => l.status === 'verified').length;
 const rejected = data.filter((l) => l.status === 'rejected').length;
 setStats({ pending, verified, rejected });
 } catch {}
 };


 const loadProjectData = async (projectId) => {
 try {
 // Get contractor's assigned tasks first
 const tasksRes = await axios.get('/tasks');
 const myTasks = tasksRes.data;
 const myUnitIds = new Set(myTasks.map(t => t.unit_id));
 
 const bRes = await axios.get(`/buildings?project_id=${projectId}`);
 setBuildings(bRes.data);
 const allUnits = [];
 for (const b of bRes.data) {
 const uRes = await axios.get(`/units?building_id=${b.id}`);
 // Only include units where contractor has assigned tasks
 const contractorUnits = uRes.data.filter(u => myUnitIds.has(u.id));
 allUnits.push(...contractorUnits);
 }
 setUnits(allUnits);
 await reloadStats(projectId);
 } catch (e) { toast.error('Failed to load project data'); }
 };

 const handleFileChange = (e) => {
 const files = Array.from(e.target.files || []).map((f) => ({ name: f.name, blob: f }));
 setForm((f) => ({ ...f, files }));
 };

 const submit = async (e) => {
 e.preventDefault();
 const isOffline = !navigator.onLine;
 const id = 'q-' + crypto.randomUUID();
 const item = {
 id,
 type: 'progress_log',
 status: 'queued',
 createdAt: Date.now(),
 files: form.files,
 payload: {
 project_id: selectedProject,
 unit_id: form.unit_id,
 trade: form.trade,
 qty_done: parseFloat(form.qty_done || 0),
 uom: form.uom,
 pct_complete: parseFloat(form.pct_complete || 0),
 notes: form.notes,
 }
 };
 if (isOffline) {
 await addQueueItem(item);
 toast.success('Queued: Progress log (will sync when online)');
 return;
 }
 // Online: upload now using the same flow as queue flush
 try {
 // Upload photos
 const photoIds = [];
 for (const f of form.files) {
 const fd = new FormData();
 fd.append('file', f.blob, f.name || 'photo.jpg');
 fd.append('project_id', selectedProject);
 fd.append('doc_type', 'PHOTOS');
 fd.append('related_entity_type', 'progress_log');
 const up = await axios.post('/cycle3/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
 if (up?.data?.id) photoIds.push(up.data.id);
 }
 const body = { ...item.payload, photos: photoIds };
 await axios.post('/cycle3/progress-logs', body);
 toast.success('Progress log submitted');
 await reloadStats(selectedProject);
 } catch (e2) {
 toast.error(e2.response?.data?.detail || 'Failed to submit');
 }
 };

 return (
 <div className="space-y-4">
 <div className="flex items-center justify-between">
 <h2 className="text-xl font-semibold">Progress Logs</h2>
 <div className="flex items-center gap-2 text-sm">
 <Badge variant="outline">Pending: {stats.pending}</Badge>
 <Badge variant="outline">Verified: {stats.verified}</Badge>
 <Badge variant="outline">Rejected: {stats.rejected}</Badge>
 </div>
 </div>
 <form onSubmit={submit} className="space-y-3">
 <div className="grid grid-cols-2 gap-3">
 <div>
 <Label>Project</Label>
 <Select value={selectedProject} onValueChange={setSelectedProject}>
 <SelectTrigger>
 <SelectValue />
 </SelectTrigger>
 <SelectContent>
 {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>Unit</Label>
 <Select value={form.unit_id} onValueChange={(v) => setForm((f) => ({ ...f, unit_id: v }))}>
 <SelectTrigger>
 <SelectValue placeholder="Select unit" />
 </SelectTrigger>
 <SelectContent>
 {units.map(u => <SelectItem key={u.id} value={u.id}>{u.code}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 </div>
 <div className="grid grid-cols-2 gap-3">
 <div>
 <Label>Trade</Label>
 <Select value={form.trade} onValueChange={(v) => setForm((f) => ({ ...f, trade: v }))}>
 <SelectTrigger>
 <SelectValue placeholder="Select trade" />
 </SelectTrigger>
 <SelectContent>
 {trades.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
 </SelectContent>
 </Select>
 </div>
 <div>
 <Label>UoM</Label>
 <Input value={form.uom} onChange={(e) => setForm((f) => ({ ...f, uom: e.target.value }))} placeholder="m2 / pcs / hours" />
 </div>
 </div>
 <div className="grid grid-cols-3 gap-3">
 <div>
 <Label>Quantity</Label>
 <Input type="number" step="0.01" value={form.qty_done} onChange={(e) => setForm((f) => ({ ...f, qty_done: e.target.value }))} />
 </div>
 <div>
 <Label>% Complete</Label>
 <Input type="number" min="0" max="100" value={form.pct_complete} onChange={(e) => setForm((f) => ({ ...f, pct_complete: e.target.value }))} />
 </div>
 <div>
 <Label>Photos</Label>
 <Input type="file" accept="image/*" capture="environment" multiple onChange={handleFileChange} />
 </div>
 </div>
 <div>
 <Label>Notes</Label>
 <Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
 </div>
 <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Submit</Button>
 </form>
 </div>
 );
}
